import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  type ActiveFilterPart,
  EntityRefCell,
  ExportMenu,
  ListPageShell,
  PageHeader,
  Table,
  focusFirstFilter,
  listEmptyContent,
  listEmptyShellProps,
} from '~/components/data';
import { HeroMetric, ItemPicker, WarehousePicker } from '~/components/domain';
import { Button, DatePicker, Input } from '~/components/primitives';
import { StatusBadge } from '~/components/status';
import { fetchItem, fetchWarehouses } from '~/lib/api/inventory';
import { fetchStockBalance } from '~/lib/api/stock';
import { formatDate, formatDecimal, formatUom } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import shared from './_auth._shared.module.css';
import styles from './_auth.stock.balance.module.css';

/** Строка без оценки — ставка пустая или ноль (журнал пишет «0.0000»). */
function isUnvalued(rate: string | null | undefined): boolean {
  return !rate || Number(rate) === 0;
}

export const Route = createFileRoute('/_auth/stock/balance')({
  staticData: { minMainWidth: 960 },
  component: StockBalance,
});

function StockBalance(): JSX.Element {
  const [asOf, setAsOf] = useState('');
  const [item, setItem] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [batch, setBatch] = useState('');
  /*
   * TCA-F2 — метка «Только строки без оценки» — отбор сервера (`unvalued`), а не фильтр
   * загруженной страницы: на странице из 100 строк строк без оценки могло не оказаться,
   * и экран писал «ничего не найдено» под плиткой «Строк без оценки: N».
   */
  const [onlyUnvalued, setOnlyUnvalued] = useState(false);

  const filters = {
    ...(asOf && { as_of: asOf }),
    ...(item && { item }),
    ...(warehouse && { warehouse }),
    ...(batch && { batch }),
    ...(onlyUnvalued && { unvalued: 'true' as const }),
  };

  const q = useQuery({
    queryKey: ['stock', 'balance', filters],
    queryFn: () => fetchStockBalance(filters),
  });
  const state = useListPageState(q);

  /*
   * Гейт F59 (D-UX-1…3): метрики — по всей выборке под фильтрами, их считает
   * сервер (`summary`). Раньше плитки суммировали видимую страницу во float и
   * подписывались «на странице»: при пагинации «Общая стоимость» была случайным
   * числом, которое читалось как стоимость склада.
   */
  const shownRows = q.data?.items?.length ?? 0;
  const summary = q.data?.summary;

  /*
   * TCA-F2 — отбор словами для пустой выдачи: «Склад: …», «Позиция: …», метка «Только
   * строки без оценки». Имена — из тех же справочников, что у полей отбора, а не id.
   */
  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: fetchWarehouses,
    enabled: warehouse !== '',
  });
  const itemRef = useQuery({
    queryKey: ['items', item],
    queryFn: () => fetchItem(item),
    enabled: item !== '',
  });
  const serverFilters: ActiveFilterPart[] = [
    ...(asOf ? [{ label: m.route_stock_filter_as_of(), value: formatDate(asOf) }] : []),
    ...(item ? [{ label: m.route_stock_filter_item_id(), value: itemRef.data?.name ?? item }] : []),
    ...(warehouse
      ? [
          {
            label: m.route_stock_filter_warehouse_id(),
            value:
              (warehouses.data?.items ?? []).find((w) => w.id === warehouse)?.name ?? warehouse,
          },
        ]
      : []),
    ...(batch ? [{ label: m.route_stock_filter_batch(), value: batch }] : []),
    ...(onlyUnvalued ? [{ label: m.stock_balance_unvalued_filter_chip() }] : []),
  ];
  const filterRowRef = useRef<HTMLDivElement>(null);
  const resetFilters = (): void => {
    setAsOf('');
    setItem('');
    setWarehouse('');
    setBatch('');
    setOnlyUnvalued(false);
    focusFirstFilter(filterRowRef.current);
  };
  // TCA-F2 (круг 1 гейта, UX) — сколько строк найдётся без отбора, для пустого состояния.
  const unfiltered = useQuery({
    queryKey: ['stock', 'balance', {}],
    queryFn: () => fetchStockBalance({}),
    enabled: serverFilters.length > 0 && state.kind === 'empty',
  });
  const emptyBalance = {
    title: m.route_stock_balance_empty_title(),
    description: m.route_stock_balance_empty_description(),
  };

  /**
   * Валюта экрана — одна для hero и колонок. Выделена в одно место, чтобы
   * «193,1 млн ₸» сверху и суммы в таблице не могли разойтись символом.
   * Мультивалютные остатки — отдельная задача валюационного пака.
   */
  const currency = 'KZT' as const;
  /** Глиф валюты для подписей колонок — тот же, что печатает денежный формат. */
  const CURRENCY_GLYPH = '₸';

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_stock_balance()}
        description={m.route_stock_balance_description()}
        actions={<ExportMenu resource="stock-balance" filters={filters} />}
      />
      {/* FE-151 + FE-246 Pass 38: 3 Hero metrics — total positions, page-level stock value, unvalued count.
          True totals require BE-side aggregate endpoint (paginated rows give только page-sum). */}
      {/*
        F9 [WHS-F12] — оговорка «на странице» относится ко ВСЕМ трём числам, а
        стояла только под вторым. Под соседними оставалась пустота, и читалось
        это как «эти два — по всей базе, а средний — по странице». Оговорка
        одна на всех, потому что ограничение одно: страница.
      */}
      {summary && (
        <div className={shared.heroRow}>
          {/* Гейт F59 (D2-UI-1, D2-UX-2): подпись — одна строка; «на экране» — только
              когда показана не вся выборка. */}
          <HeroMetric
            label={m.stock_balance_hero_rows()}
            value={summary.rows}
            subline={
              shownRows < summary.rows
                ? m.stock_balance_hero_rows_sub_shown({
                    items: String(summary.distinct_items),
                    shown: String(shownRows),
                  })
                : m.stock_balance_hero_rows_sub_items({ items: String(summary.distinct_items) })
            }
          />
          <HeroMetric
            label={m.stock_balance_hero_value()}
            value={summary.value ?? m.stock_balance_hero_value_mixed()}
            compact
            subline={
              summary.unvalued_rows > 0
                ? m.stock_balance_hero_value_sub_partial({
                    date: formatDate(summary.as_of),
                    rows: String(summary.unvalued_rows),
                  })
                : m.stock_balance_hero_value_sub({ date: formatDate(summary.as_of) })
            }
          />
          {/* Гейт F59 (D2-UX-1, D2-UI-5): предупреждение ведёт к строкам — отбор
              «только без оценки»; значок, а не только цвет рамки. */}
          <HeroMetric
            label={m.stock_balance_hero_unvalued_rows()}
            value={summary.unvalued_rows}
            subline={m.stock_balance_hero_unvalued_sub({ rows: String(summary.rows) })}
            severity={summary.unvalued_rows > 0 ? 'warn' : undefined}
            {...(summary.unvalued_rows > 0 ? { icon: AlertTriangle } : {})}
            {...(summary.unvalued_rows > 0
              ? {
                  action: {
                    label: onlyUnvalued
                      ? m.stock_balance_unvalued_show_all()
                      : m.stock_balance_unvalued_show(),
                    onClick: () => setOnlyUnvalued((v) => !v),
                    pressed: onlyUnvalued,
                  },
                }
              : {})}
          />
        </div>
      )}
      <section className={shared.sectionCard} aria-label={m.route_stock_filters_heading()}>
        {/* FE-215 Pass 15: domain pickers вместо raw UUID input. Раньше юзер
            должен был знать UUID позиции/склада — теперь поиск по name/SKU. */}
        {/* Гейт F59 (D2-UI-2, D2-UX-4): фильтры — одной строкой; подписи полей
            называют себя, отдельный заголовок «Фильтры» съедал высоту. */}
        <div className={styles.filterRow} ref={filterRowRef}>
          <DatePicker
            label={m.route_stock_filter_as_of()}
            value={asOf || null}
            onChange={(v) => setAsOf(v ?? '')}
          />
          <ItemPicker
            label={m.route_stock_filter_item_id()}
            value={item}
            onChange={(v) => setItem(v ?? '')}
          />
          <WarehousePicker
            label={m.route_stock_filter_warehouse_id()}
            value={warehouse}
            onChange={(v) => setWarehouse(v ?? '')}
          />
          <Input
            label={m.route_stock_filter_batch()}
            value={batch}
            onChange={(e) => setBatch(e.target.value)}
            placeholder={m.route_stock_filter_batch_placeholder()}
            className={shared.mono}
          />
        </div>
        {/* Гейт F59 (D3-UX-1): включённый отбор виден там, где смотрят таблицу, —
            меткой со сбросом и счётчиком «показано N из M». */}
        {/* TCA-F2, круг 2 гейта (UI): общий сброс отбора — в карточке отбора, а не только в
            пустом состоянии, которое на 1366×768 уходит под край экрана. */}
        {serverFilters.length > 0 && (
          <div className={styles.activeFilter}>
            {onlyUnvalued && summary && (
              <>
                <span className={styles.activeFilterChip}>
                  {m.stock_balance_unvalued_filter_chip()}
                  <button
                    type="button"
                    className={styles.activeFilterClear}
                    aria-label={m.stock_balance_unvalued_show_all()}
                    onClick={() => setOnlyUnvalued(false)}
                  >
                    ×
                  </button>
                </span>
                <span className={styles.activeFilterCount}>
                  {m.stock_balance_shown_of({
                    shown: String(summary.unvalued_rows),
                    total: String(summary.rows),
                  })}
                </span>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              {m.list_filter_reset()}
            </Button>
          </div>
        )}
      </section>
      {/*
        TCA-F2 — пустая выдача под отбором называет отбор и сбрасывает его. Все отборы,
        включая метку «Только строки без оценки», — отборы сервера: пусто = пусто по всей
        выборке, а не по загруженной странице.
      */}
      <ListPageShell
        state={state}
        {...listEmptyShellProps(
          listEmptyContent({
            filters: serverFilters,
            onResetFilters: resetFilters,
            unfilteredTotal: unfiltered.data?.summary?.rows,
            empty: emptyBalance,
          }),
        )}
      >
        {(rows) => (
          <Table
            ariaLabel={m.nav_stock_balance()}
            data={rows}
            rowKey={(r, i) => `${r.item_id}-${r.warehouse_id}-${r.batch_no ?? 'none'}-${i}`}
            stickyFirstColumn
            columns={[
              {
                id: 'item',
                header: m.route_stock_col_item(),
                sticky: true,
                accessor: (r) => <EntityRefCell name={r.item_name} code={r.item_sku} />,
              },
              {
                id: 'wh',
                header: m.route_stock_col_warehouse(),
                accessor: (r) => <EntityRefCell name={r.warehouse_name} code={r.warehouse_code} />,
              },
              {
                id: 'batch',
                header: m.route_stock_col_batch(),
                accessor: (r) => r.batch_no ?? '—',
                mono: true,
              },
              {
                id: 'qty',
                header: m.route_stock_col_qty(),
                // S-FIX-5-FE.5.2: drill-to-ledger по item × warehouse × batch.
                // FE-214: formatDecimal обрезает trailing zeros (10 digits → 3-4).
                /**
                 * F8 [WHS-F9] — количество перестало быть акцентным в КАЖДОЙ
                 * строке.
                 *
                 * Ревьюер назвал это «янтарь как фон», и причина оказалась не в
                 * семантике, а в устройстве ячейки: она целиком была ссылкой на
                 * журнал, а ссылки красятся акцентом. Переход полезен и
                 * остаётся, но цвет в колонке чисел обязан значить отклонение
                 * (§1.3), а не «здесь можно кликнуть» — это говорят курсор и
                 * подчёркивание при наведении.
                 */
                accessor: (r) => (
                  <Link
                    to="/stock/ledger"
                    search={{
                      item: r.item_id,
                      warehouse: r.warehouse_id,
                      ...(r.batch_id ? { batch: r.batch_id } : {}),
                    }}
                    className={styles.qtyLink}
                  >
                    {/*
                      Раунд 4, UI-3 — количество не печатает хвост деления.
                      «284,2105» и «33,3333» — это не точность, а остаток
                      арифметики: складских единиц с четырьмя знаками не
                      бывает, и лишние разряды мешают сравнивать столбец.
                    */}
                    {formatDecimal(r.qty, { maxDecimals: 3 })}
                  </Link>
                ),
                mono: true,
                align: 'right',
              },
              {
                id: 'uom',
                header: m.route_stock_col_uom(),
                // Единица — слово, а не число: «шт», «пог. м». Моноширинный
                // принадлежит цифрам и кодам (тот же корень, что у снятого
                // `mono` с подписей колонок).
                accessor: (r) => formatUom(r.uom),
              },
              {
                id: 'rate',
                header: m.route_stock_col_rate_with_currency({ currency: CURRENCY_GLYPH }),
                /**
                 * Гейт C, раунд 2, UI-3 — деньги печатаются ДЕНЬГАМИ.
                 *
                 * Стоял `formatDecimal`, то есть по сути `String(number)`:
                 * «64177396.24» — без разрядов, с точкой вместо запятой, с
                 * плавающим числом знаков. Над этой же таблицей hero говорил
                 * «193,1 млн ₸». Один экран, два языка чисел — и оператор,
                 * сверяющий их глазами, вынужден переводить в уме.
                 */
                // Символ валюты — в ПОДПИСИ колонки: он одинаков во всех
                // ячейках, и повторённый 184 раза перестаёт нести смысл.
                // Гейт F59 (D2-UX-1): строка без оценки помечена в самой ячейке цены.
                accessor: (r) =>
                  isUnvalued(r.valuation_rate) ? (
                    <StatusBadge
                      status="needs_review"
                      size="sm"
                      label={m.stock_balance_unvalued_mark()}
                    />
                  ) : (
                    formatDecimal(r.valuation_rate, { maxDecimals: 2, minDecimals: 2 })
                  ),
                mono: true,
                align: 'right',
              },
              {
                id: 'value',
                header: m.route_stock_col_value_with_currency({ currency: CURRENCY_GLYPH }),
                // Гейт F59 (D3-UI-1, D3-UX-2): у строки без оценки суммы нет — «—», а не
                // «0,00», который читается как посчитанная оценка в ноль.
                accessor: (r) =>
                  isUnvalued(r.valuation_rate) ? (
                    <span className={styles.noValue}>—</span>
                  ) : (
                    formatDecimal(r.stock_value, { maxDecimals: 2, minDecimals: 2 })
                  ),
                mono: true,
                align: 'right',
              },
            ]}
          />
        )}
      </ListPageShell>
    </div>
  );
}
